import { useEffect, useRef } from 'react';

/**
 * The sign-in panel's field of squares.
 *
 * A still grid, lit by rings expanding out from the corner the wordmark sits
 * in — one upload leaving for everywhere, which is the only thing this screen
 * has to say before you have signed in.
 *
 * The grid itself never moves. Only brightness and size change as a wavefront
 * passes, which is what makes it usable behind type: index.css commits the app
 * to "nothing moves under text being read", and a drifting texture under the
 * headline would break that on the one screen a platform reviewer sees first.
 *
 * Canvas rather than DOM: around 900 squares redrawn each frame is nothing for
 * a canvas and 900 elements for the browser's layout engine. It only ever runs
 * while signed out, so it costs the owner nothing during normal use, and
 * requestAnimationFrame already stops while the tab is hidden.
 */

/** Seconds for one wavefront to cross the panel. Two are in flight at once. */
const PERIOD = 7.4;
/** Gap between squares, in CSS pixels. */
const STEP = 18;
/** A moment mid-wave, used as the single frame under reduced motion. */
const STILL_AT = 2.6;

export function PanelField() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let frame = 0;

    // The panel is black in both themes, but the ink still comes from the
    // token rather than a literal — tokens.css stays the source of colour.
    const ink =
      getComputedStyle(document.documentElement).getPropertyValue('--aw-on-panel').trim() ||
      '#ffffff';

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      // Capped at 2: a 3x phone screen triples the fill cost for a difference
      // nobody can see on squares this small.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = (t: number) => {
      if (!width || !height) return;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = ink;

      // Where the wordmark sits, so the waves read as leaving from it.
      const originX = width * 0.13;
      const originY = height * 0.1;
      const reach = Math.hypot(width, height) * 1.05;
      const band = reach * 0.13;

      const first = ((t % PERIOD) / PERIOD) * reach;
      const second = (((t + PERIOD / 2) % PERIOD) / PERIOD) * reach;

      for (let x = STEP / 2; x < width; x += STEP) {
        for (let y = STEP / 2; y < height; y += STEP) {
          const distance = Math.hypot(x - originX, y - originY);

          let lit = 1 - Math.abs(distance - first) / band;
          const other = 1 - Math.abs(distance - second) / band;
          if (other > lit) lit = other;
          if (lit < 0) lit = 0;

          // Squared, so the crest is a bright edge rather than a wide smear.
          const e = lit * lit;
          // The wave loses strength as it travels, which keeps the far corner
          // — where the destination list sits — quiet. At full brightness all
          // the way out, the crest washed straight over "Facebook" and
          // "LinkedIn" every few seconds.
          const falloff = 1 - 0.5 * (distance / reach);
          const size = 1 + e * falloff * 3.4;
          // The resting 0.06 is what keeps the grid visible between waves —
          // without it the panel is empty for seconds at a time and each wave
          // arrives out of nowhere.
          ctx.globalAlpha = 0.06 + e * falloff * 0.38;
          ctx.fillRect(x - size / 2, y - size / 2, size, size);
        }
      }

      ctx.globalAlpha = 1;
    };

    // People who ask for no motion mean it: one frame, caught where the
    // pattern looks like itself, and no loop at all.
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let lastT = STILL_AT;

    resize();

    // A resize throws away the backing store, so the still frame has to be
    // redrawn. The animated one would repaint on its own next tick, but
    // redrawing here keeps the two paths the same.
    const observer = new ResizeObserver(() => {
      resize();
      draw(lastT);
    });
    observer.observe(canvas);

    if (still) {
      draw(STILL_AT);
      return () => {
        observer.disconnect();
      };
    }

    let start: number | null = null;
    const tick = (now: number) => {
      start ??= now;
      lastT = (now - start) / 1000;
      draw(lastT);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
    />
  );
}
